import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'

const fmt  = n => Number(n||0).toLocaleString('en-US', { maximumFractionDigits: 0 })
const fmtM = n => { const v = Math.abs(Number(n||0)); if (v>=1e6) return (Number(n)/1e6).toFixed(2)+'M'; if (v>=1e3) return (Number(n)/1e3).toFixed(1)+'K'; return fmt(n) }

const C = {
  ink:'#0F1017', ink2:'#374151', sub:'#6B7280', muted:'#9CA3AF',
  paper:'#F3F4F6', surface:'#fff', border:'#E5E7EB', border2:'#D1D5DB',
  green:'#1A7A4A', gbg:'rgba(26,122,74,.07)', gline:'rgba(26,122,74,.2)',
  red:'#DC2626', rbg:'#FEF2F2', rline:'#FECACA',
  amber:'#D97706', abg:'#FFFBEB', aline:'#FDE68A',
  purple:'#7C3AED', pbg:'#F5F3FF', pline:'#DDD6FE',
}

const btnBase = { display:'inline-flex', alignItems:'center', gap:5, padding:'6px 13px', borderRadius:8, fontSize:12, fontWeight:600, border:'1px solid transparent', cursor:'pointer', fontFamily:'inherit', transition:'all .12s', whiteSpace:'nowrap' }
const btnP   = { ...btnBase, background:C.green, color:'#fff', borderColor:C.green }
const btnS   = { ...btnBase, background:C.surface, color:C.ink2, borderColor:C.border2 }
const btnSm  = { ...btnBase, padding:'5px 10px', fontSize:11 }

function Badge({ status }) {
  const map = {
    held:     { bg:'#F5F3FF', color:'#5B21B6', border:'#DDD6FE', label:'Held' },
    upcoming: { bg:'#FFFBEB', color:'#92400E', border:'#FDE68A', label:'Upcoming' },
    overdue:  { bg:'#FEF2F2', color:'#991B1B', border:'#FECACA', label:'Overdue' },
    released: { bg:'#DCFCE7', color:'#14532D', border:'#BBF7D0', label:'Released' },
    partial:  { bg:'#EFF6FF', color:'#1E40AF', border:'#BFDBFE', label:'Partial' },
    pending:  { bg:'#F9FAFB', color:'#374151', border:'#E5E7EB', label:'Not Started' },
  }
  const s = map[status] || map.pending
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:'.04em', textTransform:'uppercase', background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
      {s.label}
    </span>
  )
}

function ProgBar({ pct, color }) {
  return (
    <div style={{ height:5, background:C.border, borderRadius:3, overflow:'hidden', marginTop:4 }}>
      <div style={{ height:'100%', width:`${Math.min(pct||0,100)}%`, background:color||C.green, borderRadius:3, transition:'width .3s' }}/>
    </div>
  )
}

function KPICard({ label, value, sub, color, extra }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, width:3, height:'100%', background:color, borderRadius:'0 0 0 0' }}/>
      <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.12em', color:C.muted, marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-.02em', lineHeight:1.1, marginBottom:3, color }}>{value}</div>
      <div style={{ fontSize:11, color:C.sub }}>{sub}</div>
      {extra}
    </div>
  )
}

// ── Detail Panel ──────────────────────────────────────────
function DetailPanel({ contract, onClose, onRequestRelease, can, onPCDateSaved }) {
  if (!contract) return null
  const certPct = contract.contract_value > 0 ? Math.round((contract.certified / contract.contract_value) * 100) : 0
  const isOverdue  = contract.status === 'overdue'
  const isReleased = contract.status === 'released'
  const isPending  = contract.status === 'pending'

  const timelineItems = isReleased ? [
    { done:true,  label:'Release 1 — Practical Completion', detail:`Released · ${contract.r1_date}`, amount:`${fmt(contract.r1_amount)} SAR ✓`, color:C.green },
    { done:true,  label:'Release 2 — DLP Expiry',           detail:`Expected ${contract.r2_date}`,   amount:`${fmt(contract.r2_amount)} SAR`,   color:C.green },
  ] : isOverdue ? [
    { done:false, label:'Release 1 — OVERDUE',              detail:`Was due ${contract.r1_date} · overdue`,    amount:`${fmt(contract.r1_amount)} SAR ● Urgent`, color:C.red },
    { done:false, label:'Release 2 — DLP Expiry',           detail:`Due ${contract.r2_date}`,                  amount:`${fmt(contract.r2_amount)} SAR`,          color:C.amber },
  ] : isPending ? [
    { done:false, label:'Release 1 — Practical Completion', detail:`Estimated ${contract.r1_date}`, amount:'To be determined', color:C.muted },
    { done:false, label:'Release 2 — DLP Expiry',           detail:`Estimated ${contract.r2_date}`, amount:'To be determined', color:C.muted },
  ] : [
    { done:true,  label:'Contract Active',                   detail:'Retention accumulating with each IPC', amount:`${fmt(contract.retention_held)} SAR held`, color:C.green },
    { done:false, label:'Release 1 — Practical Completion', detail:`Due ${contract.r1_date}`,              amount:`${fmt(contract.r1_amount)} SAR (50%)`,      color:C.amber },
    { done:false, label:'Release 2 — DLP Expiry',           detail:`Due ${contract.r2_date} (12m after PC)`, amount:`${fmt(contract.r2_amount)} SAR (50%)`,   color:C.purple },
  ]

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:199 }}/>
      <div style={{ position:'fixed', top:0, right:0, width:420, height:'100vh', background:C.surface, borderLeft:`1px solid ${C.border}`, zIndex:200, display:'flex', flexDirection:'column', boxShadow:'-4px 0 24px rgba(0,0,0,.08)', transform:'translateX(0)', transition:'transform .22s cubic-bezier(.22,1,.36,1)' }}>
        {/* Header */}
        <div style={{ padding:'16px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:C.ink }}>{contract.contract_no} · {contract.contractor_name}</div>
            <div style={{ fontSize:11, color:C.sub, marginTop:2 }}>{contract.scope}</div>
          </div>
          <button onClick={onClose} style={{ width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, cursor:'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:18 }}>
          {/* Contract Details */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:8, paddingBottom:6, borderBottom:`1px solid ${C.border}` }}>Contract Details</div>
            {[
              ['Contract No.', <span style={{ fontFamily:'monospace', fontSize:12 }}>{contract.contract_no}</span>],
              ['Contractor', contract.contractor_name],
              ['Scope', contract.scope],
              ['Status', <Badge status={contract.status}/>],
              ['Contract Value', fmt(contract.contract_value)+' SAR'],
              ['Certified to Date', <span style={{ color:C.green, fontWeight:700 }}>{fmt(contract.certified)+' SAR'}</span>],
            ].map(([l,v],i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', fontSize:12.5, borderBottom:'1px solid #F9FAFB' }}>
                <span style={{ color:C.sub }}>{l}</span>
                <span style={{ fontWeight:600, color:C.ink }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop:6 }}>
              <ProgBar pct={certPct} color={C.green}/>
              <div style={{ fontSize:10, color:C.muted, marginTop:3 }}>{certPct}% certified</div>
            </div>
          </div>

          {/* Retention Breakdown */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:8, paddingBottom:6, borderBottom:`1px solid ${C.border}` }}>Retention Breakdown</div>
            {[
              ['Retention Rate', `${contract.retention_pct||5}%`],
              ['Total Withheld', <span style={{ color:C.purple, fontSize:16, fontWeight:800 }}>{fmt(contract.retention_held)+' SAR'}</span>],
              ['Release 1 (50%)', `${fmt(contract.r1_amount)} SAR · ${contract.r1_date}`],
              ['Release 2 (50%)', `${fmt(contract.r2_amount)} SAR · ${contract.r2_date}`],
            ].map(([l,v],i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', fontSize:12.5, borderBottom:'1px solid #F9FAFB' }}>
                <span style={{ color:C.sub }}>{l}</span>
                <span style={{ fontWeight:600, color:C.ink }}>{v}</span>
              </div>
            ))}
            {/* PC Date Edit */}
            <div style={{ marginTop:10, padding:'8px 10px', background:'#F9FAFB', borderRadius:7, border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:6 }}>Practical Completion Date</div>
              <input type="date" defaultValue={contract.practical_completion_date||''}
                disabled={!can('Retention.SetPCDate')}
                onChange={async e => {
                  const val = e.target.value
                  await supabase.from('subcontracts').update({ practical_completion_date: val||null }).eq('id', contract.id)
                  // Update the parent's local contract data immediately so
                  // reopening this panel (without a full page reload) shows
                  // the saved value instead of the stale in-memory copy --
                  // defaultValue only reads once on first render, it does
                  // not track later prop changes to the same contract.
                  onPCDateSaved?.(contract.id, val||null)
                }}
                style={{ width:'100%', padding:'6px 10px', border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12.5, color:C.ink, background: can('Retention.SetPCDate') ? C.surface : '#F3F4F6', outline:'none', fontFamily:'inherit', boxSizing:'border-box', cursor: can('Retention.SetPCDate') ? 'text' : 'not-allowed' }}
                onFocus={e=>e.target.style.borderColor=C.green}
                onBlur={e=>e.target.style.borderColor=C.border2}/>
              <div style={{ fontSize:10, color:C.muted, marginTop:4 }}>R1 = PC Date · R2 = PC Date + {contract.dlp_months||12} months</div>
            </div>
          </div>

          {/* Release Schedule */}
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:8, paddingBottom:6, borderBottom:`1px solid ${C.border}` }}>Release Schedule</div>
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {timelineItems.map((item, i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', position:'relative', paddingBottom: i < timelineItems.length-1 ? 14 : 0 }}>
                  {i < timelineItems.length-1 && <div style={{ position:'absolute', left:9, top:20, bottom:0, width:1, background:C.border }}/>}
                  <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${item.color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:700, flexShrink:0, background:item.done?item.color:C.surface, color:item.done?'#fff':item.color }}>
                    {item.done?'✓':'○'}
                  </div>
                  <div style={{ flex:1, paddingTop:1 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.ink }}>{item.label}</div>
                    <div style={{ fontSize:11, color:C.sub, marginTop:1 }}>{item.detail}</div>
                    <div style={{ fontSize:11.5, fontWeight:700, color:item.color, marginTop:2 }}>{item.amount}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {isOverdue && (
              <button onClick={()=>onRequestRelease(contract, true)}
                style={{ ...btnP, justifyContent:'center', background:C.red, borderColor:C.red }}>
                ⚠ Request Urgent Release
              </button>
            )}
            {contract.status === 'held' && (
              <button onClick={()=>onRequestRelease(contract, false)} style={{ ...btnP, justifyContent:'center' }}>
                Request Release
              </button>
            )}
            <button style={{ ...btnS, justifyContent:'center' }}>+ Add Note</button>
            <button style={{ ...btnS, justifyContent:'center' }}>↓ Download Certificate</button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Timeline Tab ──────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar','Apr','May','Jun','Jul']
const MONTH_YEARS = ['Jan \'26','Feb','Mar','Apr','May','Jun ◀','Jul','Aug','Sep','Oct','Nov','Dec','Jan \'27','Feb','Mar','Apr','May','Jun','Jul']
const NOW_COL = 5 // Jun 2026 = index 5

function RetentionTimeline({ contracts }) {
  const rows = contracts.filter(c => c.status !== 'pending')
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:20, overflowX:'auto' }}>
      <div style={{ minWidth:700 }}>
        {/* Month headers */}
        <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, marginBottom:24 }}>
          {MONTH_YEARS.map((m, i) => (
            <div key={i} style={{ flex:1, textAlign:'center', fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:i===NOW_COL?C.green:C.muted, padding:'4px 0', borderRight: i<MONTH_YEARS.length-1?`1px solid ${C.border}`:'none', background:i===NOW_COL?C.gbg:'transparent' }}>
              {m}
            </div>
          ))}
        </div>

        {/* Contract rows */}
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {rows.map((c, idx) => {
            const isOverdue  = c.status === 'overdue'
            const isReleased = c.status === 'released'
            const barBg      = isOverdue ? '#FEE2E2' : isReleased ? '#DCFCE7' : '#EDE9FE'
            const barBorder  = isOverdue ? C.rline : isReleased ? C.gline : C.pline
            const labelColor = isOverdue ? C.red : isReleased ? C.green : C.ink2
            const pct1 = c.r1_col / 18 * 100
            const pct2 = c.r2_col / 18 * 100
            const dot1Color  = isOverdue ? C.red : C.amber
            const dot2Color  = isReleased ? C.green : C.purple
            return (
              <div key={idx} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:160, fontSize:11.5, fontWeight:600, color:labelColor, textAlign:'right', flexShrink:0 }}>
                  {c.contract_no} {c.contractor_name.split(' ').slice(0,2).join(' ')}{isOverdue?' ⚠':isReleased?' ✓':''}
                </div>
                <div style={{ flex:1, position:'relative', height:36 }}>
                  {/* Contract bar */}
                  <div style={{ position:'absolute', left:0, width:`${c.progress_pct||60}%`, height:14, top:11, background:barBg, borderRadius:4, border:`1px solid ${barBorder}` }}/>
                  {/* R1 dot */}
                  <div style={{ position:'absolute', left:`${pct1}%`, transform:'translateX(-50%)', top:10, width:16, height:16, borderRadius:'50%', background:dot1Color, border:'2px solid #fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff', cursor:'pointer', zIndex:1 }}>
                    {isOverdue?'!':'R1'}
                  </div>
                  <div style={{ position:'absolute', left:`${pct1}%`, transform:'translateX(-50%)', top:28, fontSize:9, fontWeight:700, color:dot1Color, whiteSpace:'nowrap' }}>
                    {fmtM(c.r1_amount)}{isOverdue?' OVERDUE':' · '+c.r1_date.split(' ')[0]}
                  </div>
                  {/* R2 dot */}
                  <div style={{ position:'absolute', left:`${pct2}%`, transform:'translateX(-50%)', top:10, width:16, height:16, borderRadius:'50%', background:dot2Color, border:'2px solid #fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff', cursor:'pointer', zIndex:1 }}>
                    {isReleased?'R2':'R2'}
                  </div>
                  <div style={{ position:'absolute', left:`${pct2}%`, transform:'translateX(-50%)', top:28, fontSize:9, fontWeight:600, color:dot2Color, whiteSpace:'nowrap' }}>
                    {fmtM(c.r2_amount)} · {c.r2_date.split(' ')[0]}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Today line */}
        <div style={{ position:'absolute', left:`calc(160px + 12px + (100% - 172px) * ${NOW_COL}/18)`, top:0, bottom:0, width:1.5, background:C.green, opacity:.6, pointerEvents:'none' }}/>

        {/* Legend */}
        <div style={{ display:'flex', gap:20, marginTop:32, paddingTop:14, borderTop:`1px solid ${C.border}`, flexWrap:'wrap' }}>
          {[
            [C.amber,  'R1 — Release at Practical Completion'],
            [C.purple, 'R2 — Release after Defects Liability Period'],
            [C.red,    '! Overdue release'],
            [C.green,  '✓ Released'],
          ].map(([color, label]) => (
            <span key={label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:C.sub }}>
              <span style={{ width:14, height:14, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }}/>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────
export default function RetentionManagement({ projectId: initProjectId }) {
  const [projects,  setProjects]  = useState([])
  const [projectId, setProjectId] = useState(initProjectId || null)
  const [projectName, setProjectName] = useState('')
  const { can } = useProjectAuth(projectId)
  const [contracts, setContracts] = useState([])
  const [releases,  setReleases]  = useState([])
  const [loading,   setLoading]   = useState(false)
  const [tab,       setTab]       = useState('contracts')
  const [detail,    setDetail]    = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [showRelease, setShowRelease] = useState(null)
  const [releaseMode, setReleaseMode] = useState('record') // 'request' | 'record'
  const [releaseForm, setReleaseForm] = useState({ amount:'', date:'', type:'Release 1 — Practical Completion', notes:'' })
  const [editRelease, setEditRelease] = useState(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)

  // Sync initProjectId when it changes
  useEffect(() => { if (initProjectId) setProjectId(initProjectId) }, [initProjectId])

  useEffect(() => { loadProjects() }, [])
  useEffect(() => { if (projectId) loadData(projectId) }, [projectId])

  async function loadProjects() {
    const { data } = await supabase.from('projects').select('id,name,organizations(name)').order('name')
    setProjects(data || [])
    if (initProjectId) {
      const p = (data||[]).find(x => x.id === initProjectId)
      if (p) setProjectName(p.name)
    }
  }

  // Keeps contracts[] and the open DetailPanel's contract prop in sync
  // right after a successful PC Date save, without a full loadData()
  // reload. Must recompute the DERIVED timeline fields (r1Date, r2Date,
  // r1Col, r2Col, status) too -- they are calculated once inside
  // loadData() from practical_completion_date, and do not update on
  // their own just because the raw date field changes.
  function handlePCDateSaved(contractId, newDate) {
    function recompute(c) {
      const pcd = newDate ? new Date(newDate) : null
      const dlp = parseInt(c.dlp_months) || 12
      let r1_date = '—', r2_date = '—', r1_col = 0, r2_col = 0
      let status = c.status
      if (pcd) {
        r1_date = pcd.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        const r2 = new Date(pcd)
        r2.setMonth(r2.getMonth() + dlp)
        r2_date = r2.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        r1_col = pcd.getMonth() + (pcd.getFullYear() - 2026) * 12
        r2_col = r2.getMonth() + (r2.getFullYear() - 2026) * 12
        if (pcd < new Date() && (c.retention_held||0) > 0) status = 'overdue'
        else if (status === 'overdue') status = c.retention_held > 0 ? 'held' : 'released'
      } else {
        status = c.status === 'overdue' ? 'held' : c.status
      }
      return { ...c, practical_completion_date: newDate, r1_date, r2_date, r1_col, r2_col, status }
    }
    setContracts(prev => prev.map(c => c.id === contractId ? recompute(c) : c))
    setDetail(prev => prev && prev.id === contractId ? recompute(prev) : prev)
  }

  async function loadData(pid) {
    setLoading(true)
    try {
      // Load subcontracts with retention data
      const { data: subs } = await supabase
        .from('subcontracts')
        .select('id,contract_no,contractor_name,scope,contract_value,contract_inc_vat,retention_pct,status,practical_completion_date,dlp_months')
        .eq('project_id', pid)
        .order('contract_no')

      // Load equipment contracts with retention data (no PC date / DLP columns yet — timeline stays pending)
      const { data: eqSubs } = await supabase
        .from('equipment_contracts')
        .select('id,contract_no,contractor_name,scope,contract_value,contract_inc_vat,retention_pct,status')
        .eq('project_id', pid)
        .order('contract_no')

      // Load IPC data to get certified amounts (material + equipment, distinguished by which FK is set)
      const { data: ipcs } = await supabase
        .from('ipc_certificates')
        .select('subcontract_id, equipment_contract_id, gross_amount, retention_amount, status')
        .eq('project_id', pid)
        .in('status', ['approved', 'paid'])

      // Load retention releases (material + equipment, distinguished by which FK is set)
      const { data: rels } = await supabase
        .from('retention_releases')
        .select('*')
        .eq('project_id', pid)
        .order('release_date', { ascending: false })
      setReleases(rels || [])

      // Build enriched contract data — material contracts
      const enrichedMaterial = (subs || []).map((s, idx) => {
        const subIpcs    = (ipcs||[]).filter(i => i.subcontract_id === s.id)
        const certified  = subIpcs.reduce((acc, i) => acc + (parseFloat(i.gross_amount)||0), 0)
        const retPct     = parseFloat(s.retention_pct) || 5
        // Use the actual retention_amount recorded per IPC (computed in IPC.jsx with cap + non-negative
        // floor + final true-up), instead of recomputing certified*retPct from scratch — keeps both
        // screens in sync and correct even when a Variation/Addendum changed contract_value mid-way.
        const retHeld    = subIpcs.reduce((acc, i) => acc + (parseFloat(i.retention_amount)||0), 0)
        const r1Amount   = retHeld / 2
        const r2Amount   = retHeld / 2
        // Check releases
        const contractReleases = (rels||[]).filter(r => r.subcontract_id === s.id && !r.equipment_contract_id)
        const totalReleased    = contractReleases.reduce((a, r) => a + (parseFloat(r.amount)||0), 0)
        const remaining        = Math.max(retHeld - totalReleased, 0)

        // Determine status
        let status = 'pending'
        if (certified > 0 && remaining > 0) status = 'held'
        if (remaining === 0 && retHeld > 0) status = 'released'

        // Dates from practical_completion_date
        const pcd = s.practical_completion_date ? new Date(s.practical_completion_date) : null
        const dlp = parseInt(s.dlp_months) || 12
        let r1Date = '—', r2Date = '—', r1Col = 0, r2Col = 0
        if (pcd) {
          r1Date = pcd.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
          const r2 = new Date(pcd)
          r2.setMonth(r2.getMonth() + dlp)
          r2Date = r2.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
          r1Col = pcd.getMonth() + (pcd.getFullYear() - 2026) * 12
          r2Col = r2.getMonth() + (r2.getFullYear() - 2026) * 12
          // Overdue if PC date passed and retention still held
          if (pcd < new Date() && remaining > 0) status = 'overdue'
        }

        return {
          ...s,
          contract_type: 'material',
          certified, retPct, retention_held: remaining,
          r1_amount: r1Amount, r2_amount: r2Amount,
          r1_date: r1Date, r2_date: r2Date,
          r1_col: r1Col, r2_col: r2Col,
          total_released: totalReleased,
          status,
          progress_pct: parseFloat(s.contract_value) > 0 ? (certified / parseFloat(s.contract_value)) * 100 : 0,
        }
      })

      // Build enriched contract data — equipment contracts (no PC date tracked yet, so timeline stays '—')
      const enrichedEquipment = (eqSubs || []).map((s) => {
        const subIpcs    = (ipcs||[]).filter(i => i.equipment_contract_id === s.id)
        const certified  = subIpcs.reduce((acc, i) => acc + (parseFloat(i.gross_amount)||0), 0)
        const retPct     = parseFloat(s.retention_pct) || 5
        const retHeld    = subIpcs.reduce((acc, i) => acc + (parseFloat(i.retention_amount)||0), 0)
        const r1Amount   = retHeld / 2
        const r2Amount   = retHeld / 2
        const contractReleases = (rels||[]).filter(r => r.equipment_contract_id === s.id)
        const totalReleased    = contractReleases.reduce((a, r) => a + (parseFloat(r.amount)||0), 0)
        const remaining        = Math.max(retHeld - totalReleased, 0)

        let status = 'pending'
        if (certified > 0 && remaining > 0) status = 'held'
        if (remaining === 0 && retHeld > 0) status = 'released'

        return {
          ...s,
          contract_type: 'equipment',
          certified, retPct, retention_held: remaining,
          r1_amount: r1Amount, r2_amount: r2Amount,
          r1_date: '—', r2_date: '—', r1_col: 0, r2_col: 0,
          total_released: totalReleased,
          status,
          progress_pct: parseFloat(s.contract_value) > 0 ? (certified / parseFloat(s.contract_value)) * 100 : 0,
        }
      })

      setContracts([...enrichedMaterial, ...enrichedEquipment])
    } catch(e) { console.error(e) }
    setLoading(false)
  }

  function openRelease(contract, mode='record') {
    // Auto-detect R1 or R2
    const isEquipment = contract.contract_type === 'equipment'
    const existingReleases = releases.filter(r => isEquipment ? r.equipment_contract_id === contract.id : r.subcontract_id === contract.id)
    const r1Done = existingReleases.some(r => r.release_type.includes('Release 1') || r.release_type.includes('Practical'))
    const autoType = r1Done ? 'Release 2 — DLP Expiry' : 'Release 1 — Practical Completion'
    const autoAmount = r1Done ? contract.r2_amount : contract.r1_amount
    setReleaseMode(mode)
    setShowRelease(contract)
    setReleaseForm({ amount: autoAmount ? String(Math.round(autoAmount)) : '', date: new Date().toISOString().split('T')[0], type: autoType, notes: '' })
  }

  async function handleRecordRelease() {
    if (!showRelease) return
    setSaving(true)
    try {
      const isEquipment = showRelease.contract_type === 'equipment'
      const { error } = await supabase.from('retention_releases').insert({
        project_id:      projectId,
        subcontract_id:  isEquipment ? null : showRelease.id,
        equipment_contract_id: isEquipment ? showRelease.id : null,
        contract_no:     showRelease.contract_no,
        contractor_name: showRelease.contractor_name,
        release_type:    releaseForm.type,
        amount:          parseFloat(releaseForm.amount) || 0,
        release_date:    releaseForm.date || new Date().toISOString().split('T')[0],
        notes:           releaseForm.notes,
        status:          releaseMode === 'request' ? 'requested' : 'paid',
      })
      if (error) throw error
      setShowRelease(null)
      setReleaseForm({ amount:'', date:'', type:'Release 1 — Practical Completion', notes:'' })
      setToast(releaseMode === 'request' ? 'Release requested' : 'Release recorded')
      await loadData(projectId)
    } catch(e) { alert(e.message) }
    setSaving(false)
  }

  async function handleUpdateRelease() {
    if (!editRelease) return
    setSaving(true)
    try {
      const { error } = await supabase.from('retention_releases').update({
        release_type: editRelease.release_type,
        amount:       parseFloat(editRelease.amount) || 0,
        release_date: editRelease.release_date,
        notes:        editRelease.notes,
        status:       editRelease.status,
      }).eq('id', editRelease.id)
      if (error) throw error
      setEditRelease(null)
      setToast('Release updated')
      await loadData(projectId)
    } catch(e) { alert(e.message) }
    setSaving(false)
  }

  async function handleDeleteRelease(id) {
    if (!window.confirm('Delete this release record?')) return
    await supabase.from('retention_releases').delete().eq('id', id)
    setToast('Release deleted')
    await loadData(projectId)
  }

  function showToastMsg(msg) { setToast(msg); setTimeout(() => setToast(null), 3000) }

  // Computed totals
  const totalHeld     = contracts.reduce((s, c) => s + (c.retention_held||0), 0)
  const totalReleased = contracts.reduce((s, c) => s + (c.total_released||0), 0)
  const totalExposure = contracts.reduce((s, c) => s + (c.r1_amount||0) + (c.r2_amount||0) + (c.total_released||0), 0)
  const dueContracts  = contracts.filter(c => c.status === 'overdue' || c.status === 'upcoming')
  const totalDue      = dueContracts.reduce((s, c) => s + (c.r1_amount||0), 0)
  const overdueCount  = contracts.filter(c => c.status === 'overdue').length

  const filtered = statusFilter === 'all' ? contracts : contracts.filter(c => c.status === statusFilter)

  // Project picker
  if (!projectId) return (
    <div style={{ padding:24, maxWidth:800, margin:'0 auto', fontFamily:'inherit' }}>
      <h1 style={{ fontSize:20, fontWeight:800, color:C.ink, marginBottom:6 }}>Retention Management</h1>
      <p style={{ fontSize:12, color:C.sub, marginBottom:16 }}>Select a project to continue</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectName(p.name) }}
            style={{ textAlign:'left', background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=C.green; e.currentTarget.style.boxShadow=`0 2px 12px ${C.gbg}` }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow='none' }}>
            <p style={{ fontWeight:700, color:C.ink, fontSize:13 }}>{p.name}</p>
            <p style={{ fontSize:11, color:C.muted, marginTop:2 }}>{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 48px)', fontFamily:'inherit', background:C.paper }}>

      {/* ── Page Header ── */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'18px 28px 14px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexShrink:0 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:C.ink, letterSpacing:'-.02em' }}>Retention Management</h1>
          <p style={{ fontSize:11.5, color:C.sub, marginTop:3 }}>
            {projectName} · {contracts.length} Subcontracts · As of {new Date().toLocaleDateString('en-GB',{month:'short',year:'numeric'})}
            <button onClick={() => setProjectId(null)}
              style={{ marginLeft:8, fontSize:10, color:C.muted, background:'none', border:`1px solid ${C.border}`, borderRadius:4, padding:'1px 7px', cursor:'pointer', fontFamily:'inherit' }}>
              × Change
            </button>
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={{ ...btnS, ...btnSm }}>↓ Export PDF</button>
          <button style={{ ...btnS, ...btnSm }}>↓ Excel</button>
          {can('Retention.Create') && (
            <button style={{ ...btnS, ...btnSm }} onClick={() => contracts[0] && openRelease(contracts[0], 'request')}>Request Release</button>
          )}
          {can('Retention.Create') && (
            <button style={{ ...btnP, ...btnSm }} onClick={() => contracts[0] && openRelease(contracts[0], 'record')}>+ Record Release</button>
          )}
        </div>
      </div>

      {/* ── KPI Row ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, padding:'16px 28px', background:C.surface, borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
        <KPICard
          label="Total Retention Held" value={fmtM(totalHeld)} sub={`SAR · ${contracts.filter(c=>c.retention_held>0).length} subcontracts`}
          color={C.purple}
          extra={<><ProgBar pct={totalExposure>0?(totalHeld/totalExposure)*100:0} color={C.purple}/><div style={{fontSize:10,color:C.muted,marginTop:3}}>{totalExposure>0?((totalHeld/totalExposure)*100).toFixed(0):0}% of total retention exposure ({fmtM(totalExposure)})</div></>}
        />
        <KPICard
          label="Due for Release" value={fmtM(totalDue)} sub="Practical completion reached" color={C.amber}
          extra={<div style={{marginTop:8,display:'flex',gap:6,flexWrap:'wrap'}}>{overdueCount>0&&<Badge status="overdue"/>}{dueContracts.length>0&&<span style={{padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:C.abg,color:'#92400E',border:`1px solid ${C.aline}`}}>{dueContracts.length} Upcoming</span>}</div>}
        />
        <KPICard
          label="Released to Date" value={fmtM(totalReleased)} sub={`Paid & cleared · ${releases.length} releases`}
          color={C.green}
          extra={<><ProgBar pct={totalExposure>0?(totalReleased/totalExposure)*100:0} color={C.green}/><div style={{fontSize:10,color:C.muted,marginTop:3}}>{totalExposure>0?((totalReleased/totalExposure)*100).toFixed(0):0}% of total exposure released</div></>}
        />
        <KPICard
          label="Overdue Release" value={overdueCount > 0 ? fmtM(contracts.filter(c=>c.status==='overdue').reduce((s,c)=>s+c.r1_amount,0)) : '0'}
          sub={overdueCount > 0 ? `${overdueCount} contract${overdueCount>1?'s':''} overdue` : 'All releases on track'}
          color={overdueCount > 0 ? C.red : C.green}
          extra={overdueCount>0?<div style={{marginTop:8}}><Badge status="overdue"/></div>:null}
        />
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, padding:'0 28px', background:C.surface, flexShrink:0 }}>
        {[{id:'contracts',label:'Contract Summary'},{id:'timeline',label:'Release Timeline'},{id:'history',label:'Release History'}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding:'9px 16px', fontSize:12.5, fontWeight:tab===t.id?700:500, color:tab===t.id?C.ink:C.sub, background:'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?C.green:'transparent'}`, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all .12s', marginBottom:-1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Body ── */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 28px' }}>

        {loading && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:64, gap:10, color:C.muted }}>
            <div style={{ width:24, height:24, border:`3px solid ${C.border}`, borderTopColor:C.green, borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
            Loading retention data…
          </div>
        )}

        {!loading && tab === 'contracts' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:C.ink }}>Retention by Subcontract</div>
                <div style={{ fontSize:11, color:C.sub }}>Click any row to see IPC breakdown and release schedule</div>
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ padding:'5px 10px', border:`1px solid ${C.border2}`, borderRadius:8, fontSize:12, color:C.ink, background:C.surface, outline:'none', cursor:'pointer' }}>
                <option value="all">All Status</option>
                <option value="held">Held</option>
                <option value="overdue">Overdue</option>
                <option value="released">Released</option>
                <option value="pending">Not Started</option>
              </select>
            </div>

            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
              {filtered.length === 0 ? (
                <div style={{ padding:'60px 24px', textAlign:'center', color:C.muted }}>
                  <p style={{ fontSize:14, fontWeight:600, color:C.sub }}>No subcontracts found</p>
                </div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB', borderBottom:`1px solid ${C.border}` }}>
                      {['Contract','Contractor','Ret. %','Contract Value','Certified (SAR)','Retention Held','Release 1 (50%)','Release 2 (50%)','Due Date','Status','Actions'].map((h,i) => (
                        <th key={h} style={{ padding:'9px 14px', textAlign:i>=3&&i<=7?'right':'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:C.sub, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, idx) => (
                      <tr key={c.id}
                        onClick={() => setDetail(c)}
                        style={{ background: idx%2===0?C.surface:'#FAFAFA', borderBottom:`1px solid ${C.border}`, cursor:'pointer', borderLeft: c.status==='overdue'?`3px solid ${C.red}`:'none' }}
                        onMouseEnter={e => e.currentTarget.style.background='#F3F9F6'}
                        onMouseLeave={e => e.currentTarget.style.background=idx%2===0?C.surface:'#FAFAFA'}>
                        <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11.5, paddingLeft: c.status==='overdue'?11:14 }}>
                          <span title={c.contract_type==='equipment'?'Equipment Contract':'Material Contract'}>{c.contract_type==='equipment'?'🚜':'📦'}</span> {c.contract_no}
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          <div style={{ fontWeight:600, color:C.ink }}>{c.contractor_name}</div>
                          {c.status==='overdue' && <div style={{ fontSize:10, color:C.red }}>⚠ Release overdue</div>}
                        </td>
                        <td style={{ padding:'10px 14px', fontFamily:'monospace', fontWeight:700 }}>{c.retPct}%</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(c.contract_value)}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right' }}>
                          {c.certified > 0 ? (
                            <>
                              <div style={{ fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(c.certified)}</div>
                              <ProgBar pct={c.progress_pct} color={c.progress_pct>=100?C.green:c.progress_pct>=50?C.green:C.amber}/>
                              <div style={{ fontSize:10, color:C.muted, textAlign:'right' }}>{c.progress_pct.toFixed(0)}%{c.progress_pct>=100?' ✓':''}</div>
                            </>
                          ) : <span style={{ color:C.muted, fontSize:11 }}>Not started</span>}
                        </td>
                        <td style={{ padding:'10px 14px', textAlign:'right' }}>
                          <span style={{ fontSize:13, fontWeight:800, color: c.status==='released'?C.green:c.status==='overdue'?C.red:C.purple }}>
                            {c.retention_held > 0 ? fmt(c.retention_held) : <span style={{color:C.muted}}>—</span>}
                          </span>
                          {c.status==='released' && <div style={{fontSize:10,color:C.green}}>Fully released</div>}
                        </td>
                        <td style={{ padding:'10px 14px', textAlign:'right' }}>
                          {c.r1_amount > 0 ? (
                            <>
                              <span style={{ fontSize:12, fontWeight:700, color: c.status==='released'?C.green:c.status==='overdue'?C.red:C.sub }}>{fmt(c.r1_amount)}{c.status==='released'?' ✓':''}</span>
                              <div style={{ fontSize:10, color:c.status==='overdue'?C.red:C.muted }}>{c.r1_date}{c.status==='overdue'?' ● OVERDUE':''}</div>
                            </>
                          ) : <span style={{color:C.muted}}>—</span>}
                        </td>
                        <td style={{ padding:'10px 14px', textAlign:'right' }}>
                          {c.r2_amount > 0 ? (
                            <>
                              <span style={{ fontSize:12, fontWeight:700, color:C.sub }}>{fmt(c.r2_amount)}</span>
                              <div style={{ fontSize:10, color:C.muted }}>{c.r2_date}</div>
                            </>
                          ) : <span style={{color:C.muted}}>—</span>}
                        </td>
                        <td style={{ padding:'10px 14px' }}>
                          <span style={{ fontSize:11.5, color:c.status==='overdue'?C.red:C.sub, fontWeight:c.status==='overdue'?700:400 }}>
                            {c.r1_date}{c.status==='overdue'?' ●':''}
                          </span>
                        </td>
                        <td style={{ padding:'10px 14px' }}><Badge status={c.status}/></td>
                        <td style={{ padding:'10px 14px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display:'flex', gap:4 }}>
                            <button onClick={() => setDetail(c)} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, borderRadius:4, cursor:'pointer' }}>Detail</button>
                            {c.status === 'overdue' && <button onClick={() => openRelease(c, 'request')} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:`1px solid ${C.aline}`, background:C.abg, color:C.amber, borderRadius:4, cursor:'pointer' }}>Urgent</button>}
                            {(c.status === 'held' || c.status === 'overdue') && <button onClick={() => openRelease(c, 'record')} style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:`1px solid ${C.gline}`, background:C.gbg, color:C.green, borderRadius:4, cursor:'pointer' }}>Release</button>}
                            {c.status === 'released' && <button style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, borderRadius:4, cursor:'pointer' }}>History</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr style={{ background:C.ink }}>
                      <td colSpan={3} style={{ padding:'9px 14px', fontWeight:700, color:'#fff', fontSize:11 }}>TOTAL</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:700, color:'#fff', fontVariantNumeric:'tabular-nums' }}>{fmt(contracts.reduce((s,c)=>s+parseFloat(c.contract_value||0),0))}</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:700, color:'#fff', fontVariantNumeric:'tabular-nums' }}>{fmt(contracts.reduce((s,c)=>s+c.certified,0))}</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:700, color:'#C4B5FD', fontVariantNumeric:'tabular-nums' }}>{fmt(totalHeld)}</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:700, color:'#86EFAC', fontVariantNumeric:'tabular-nums' }}>{fmt(contracts.reduce((s,c)=>s+c.r1_amount,0))}</td>
                      <td style={{ padding:'9px 14px', textAlign:'right', fontWeight:700, color:'#86EFAC', fontVariantNumeric:'tabular-nums' }}>{fmt(contracts.reduce((s,c)=>s+c.r2_amount,0))}</td>
                      <td colSpan={3}/>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {!loading && tab === 'timeline' && (
          <div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.ink }}>Retention Release Timeline — 2026 / 2027</div>
              <div style={{ fontSize:11, color:C.sub }}>Planned release dates for all active contracts</div>
            </div>
            <RetentionTimeline contracts={filtered.length > 0 ? filtered : contracts}/>
          </div>
        )}

        {!loading && tab === 'history' && (
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:C.ink, marginBottom:12 }}>Release History</div>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
              {releases.length === 0 ? (
                <div style={{ padding:'40px', textAlign:'center', color:C.muted }}>
                  <p style={{ fontSize:13, fontWeight:600, color:C.sub }}>No releases recorded yet</p>
                  {can('Retention.Create') && (
                    <button onClick={() => setShowRelease(contracts[0]||null)} style={{ ...btnP, marginTop:12 }}>+ Record First Release</button>
                  )}
                </div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB', borderBottom:`1px solid ${C.border}` }}>
                      {['Ref.','Contract','Contractor','Release Type','Amount (SAR)','Date Released','Status','Notes',''].map((h,i) => (
                        <th key={h} style={{ padding:'9px 14px', textAlign:i===4?'right':'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:C.sub, whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {releases.map((r, i) => (
                      <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.surface:'#FAFAFA' }}>
                        <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11.5 }}>RR-{String(releases.length-i).padStart(3,'0')}</td>
                        <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11.5 }}>{r.contract_no}</td>
                        <td style={{ padding:'10px 14px', fontWeight:600, color:C.ink }}>{r.contractor_name}</td>
                        <td style={{ padding:'10px 14px', color:C.ink2 }}>{r.release_type}</td>
                        <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(r.amount)}</td>
                        <td style={{ padding:'10px 14px', color:C.sub }}>{r.release_date}</td>
                        <td style={{ padding:'10px 14px' }}><Badge status={r.status==='requested'?'overdue':r.status==='paid'?'released':'pending'} label={r.status}/></td>
                        <td style={{ padding:'10px 14px', color:C.muted, fontSize:11 }}>{r.notes||'—'}</td>
                        <td style={{ padding:'10px 14px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            {can('Retention.Edit') && (
                              <button onClick={() => setEditRelease({...r})}
                                style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, borderRadius:4, cursor:'pointer' }}>Edit</button>
                            )}
                            {can('Retention.Delete') && (
                              <button onClick={() => handleDeleteRelease(r.id)}
                                style={{ padding:'3px 8px', fontSize:10.5, fontWeight:600, border:'1px solid #FCA5A5', background:'#FEF2F2', color:'#DC2626', borderRadius:4, cursor:'pointer' }}>Delete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Detail Panel ── */}
      <DetailPanel contract={detail} onClose={() => setDetail(null)} onRequestRelease={(c, urgent) => { setDetail(null); setShowRelease(c) }} can={can} onPCDateSaved={handlePCDateSaved}/>

      {/* ── Record Release Modal ── */}
      {showRelease && (
        <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div onClick={() => setShowRelease(null)} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.45)', backdropFilter:'blur(3px)' }}/>
          <div style={{ position:'relative', zIndex:1, width:480, background:C.surface, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 24px 64px rgba(0,0,0,.15)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:800, color:C.ink }}>{releaseMode==='request' ? 'Request Retention Release' : 'Record Retention Release'}</h2>
                <p style={{ fontSize:11, color:C.sub, marginTop:2 }}>{showRelease.contract_no} · {showRelease.contractor_name}</p>
              </div>
              <button onClick={() => setShowRelease(null)} style={{ width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'Release Type', k:'type', type:'select', options:['Release 1 — Practical Completion','Release 2 — DLP Expiry','Partial Release','Other'] },
                { label:'Amount (SAR)', k:'amount', type:'number', placeholder:`Max: ${fmt(showRelease.retention_held)}` },
                { label:'Release Date', k:'date', type:'date' },
                { label:'Notes', k:'notes', type:'text', placeholder:'Optional — reason or reference' },
              ].map(f => (
                <div key={f.k}>
                  <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:5 }}>{f.label}</label>
                  {f.type === 'select' ? (
                    <select value={releaseForm[f.k]} onChange={e => setReleaseForm(p => ({ ...p, [f.k]: e.target.value }))}
                      style={{ width:'100%', padding:'7px 10px', border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12.5, color:C.ink, background:C.surface, outline:'none', fontFamily:'inherit', boxSizing:'border-box', cursor:'pointer' }}>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={releaseForm[f.k]} placeholder={f.placeholder} onChange={e => setReleaseForm(p => ({ ...p, [f.k]: e.target.value }))}
                      style={{ width:'100%', padding:'7px 10px', border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12.5, color:C.ink, background:C.surface, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
                      onFocus={e => e.target.style.borderColor=C.green} onBlur={e => e.target.style.borderColor=C.border2}/>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setShowRelease(null)} style={{ ...btnS, ...btnSm }}>Cancel</button>
              <button onClick={handleRecordRelease} disabled={saving || !releaseForm.amount}
                style={{ ...btnP, ...btnSm, opacity:(saving||!releaseForm.amount)?.5:1 }}>
                {saving ? 'Saving…' : releaseMode==='request' ? 'Submit Request' : 'Record Release'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Release Modal ── */}
      {editRelease && (
        <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div onClick={() => setEditRelease(null)} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.45)', backdropFilter:'blur(3px)' }}/>
          <div style={{ position:'relative', zIndex:1, width:480, background:C.surface, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 24px 64px rgba(0,0,0,.15)', overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:800, color:C.ink }}>Edit Release</h2>
                <p style={{ fontSize:11, color:C.sub, marginTop:2 }}>{editRelease.contract_no} · {editRelease.contractor_name}</p>
              </div>
              <button onClick={() => setEditRelease(null)} style={{ width:28, height:28, borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.sub, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { label:'Release Type', k:'release_type', type:'select', options:['Release 1 — Practical Completion','Release 2 — DLP Expiry','Partial Release','Other'] },
                { label:'Amount (SAR)', k:'amount', type:'number' },
                { label:'Release Date', k:'release_date', type:'date' },
                { label:'Status', k:'status', type:'select', options:['requested','paid','cancelled'] },
                { label:'Notes', k:'notes', type:'text' },
              ].map(f => (
                <div key={f.k}>
                  <label style={{ display:'block', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', color:C.sub, marginBottom:5 }}>{f.label}</label>
                  {f.type === 'select' ? (
                    <select value={editRelease[f.k]||''} onChange={e => setEditRelease(p => ({ ...p, [f.k]: e.target.value }))}
                      style={{ width:'100%', padding:'7px 10px', border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12.5, color:C.ink, background:C.surface, outline:'none', fontFamily:'inherit', boxSizing:'border-box', cursor:'pointer' }}>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={editRelease[f.k]||''} onChange={e => setEditRelease(p => ({ ...p, [f.k]: e.target.value }))}
                      style={{ width:'100%', padding:'7px 10px', border:`1px solid ${C.border2}`, borderRadius:6, fontSize:12.5, color:C.ink, background:C.surface, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
                      onFocus={e => e.target.style.borderColor=C.green} onBlur={e => e.target.style.borderColor=C.border2}/>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setEditRelease(null)} style={{ ...btnS, ...btnSm }}>Cancel</button>
              <button onClick={handleUpdateRelease} disabled={saving}
                style={{ ...btnP, ...btnSm, opacity:saving?.5:1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:500, background:C.green, color:'#fff', padding:'10px 18px', borderRadius:8, fontSize:13, fontWeight:600, boxShadow:'0 4px 16px rgba(0,0,0,.15)' }}>
          ✓ {toast}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
